"use client";

import { useEffect, useState } from "react";
import { Icon } from "@iconify-icon/react";
import { AnimatePresence, motion } from "motion/react";
import { intervalToDuration } from "date-fns";
import { toZonedTime } from "date-fns-tz";

import useUserStore from "@/features/auth/store/useUserStore";

interface LessonCountdownTimerProps {
  endTime: string | null; // ISO string in UTC
  onTimeUp?: () => void;
}

export default function LessonCountdownTimer({ endTime, onTimeUp }: LessonCountdownTimerProps) {
  const { user } = useUserStore();
  const [timeRemaining, setTimeRemaining] = useState<{
    totalSeconds: number;
    hours: number;
    minutes: number;
    seconds: number;
  } | null>(null);
  const [isWarning, setIsWarning] = useState(false);
  const [isCritical, setIsCritical] = useState(false);

  useEffect(() => {
    if (!endTime) {
      setTimeRemaining(null);

      return;
    }

    const updateCountdown = () => {
      try {
        // Convert UTC end time to user's timezone
        const userTimezone = user?.timezone || "UTC";
        const endTimeInUserTz = toZonedTime(endTime, userTimezone);
        const now = new Date();

        // Calculate time remaining
        const timeDiff = endTimeInUserTz.getTime() - now.getTime();

        if (timeDiff <= 0) {
          setTimeRemaining(null);
          onTimeUp?.();

          return;
        }

        const totalSeconds = Math.floor(timeDiff / 1000);
        const duration = intervalToDuration({ start: 0, end: timeDiff });

        const hours = duration.hours || 0;
        const minutes = duration.minutes || 0;
        const seconds = duration.seconds || 0;

        setTimeRemaining({
          totalSeconds,
          hours,
          minutes,
          seconds,
        });

        // Set warning states
        setIsWarning(totalSeconds <= 300 && totalSeconds > 60); // 5 minutes warning
        setIsCritical(totalSeconds <= 60); // 1 minute critical
      } catch (error) {
        console.error("Error calculating countdown:", error);
        setTimeRemaining(null);
      }
    };

    // Update immediately
    updateCountdown();

    // Set up interval to update every second
    const interval = setInterval(updateCountdown, 1000);

    return () => clearInterval(interval);
  }, [endTime, user?.timezone, onTimeUp]);

  if (!timeRemaining) {
    return null;
  }

  const formatTime = () => {
    const { hours, minutes, seconds } = timeRemaining;

    if (hours > 0) {
      return `${hours}:${minutes.toString().padStart(2, "0")}:${seconds.toString().padStart(2, "0")}`;
    }

    return `${minutes}:${seconds.toString().padStart(2, "0")}`;
  };

  const getTimerColor = () => {
    if (isCritical) return "text-destructive";
    if (isWarning) return "text-yellow-500";

    return "text-accent-foreground";
  };

  const getIconColor = () => {
    if (isCritical) return "text-destructive";
    if (isWarning) return "text-yellow-500";

    return "text-muted-foreground";
  };

  return (
    <AnimatePresence>
      <motion.div
        animate={{ opacity: 1, scale: 1 }}
        className="absolute left-6 flex items-center gap-2"
        exit={{ opacity: 0, scale: 0.9 }}
        initial={{ opacity: 0, scale: 0.9 }}
        transition={{ duration: 0.3 }}
      >
        <motion.div
          animate={
            isCritical
              ? {
                  scale: [1, 1.1, 1],
                  transition: { duration: 1, repeat: Infinity },
                }
              : {}
          }
          className="flex items-center justify-center"
        >
          <Icon
            className={getIconColor()}
            height="16"
            icon={isCritical ? "lucide:alert-circle" : "lucide:clock"}
            width="16"
          />
        </motion.div>

        <motion.span
          animate={
            isCritical
              ? {
                  color: ["#ef4444", "#dc2626", "#ef4444"],
                  transition: { duration: 1, repeat: Infinity },
                }
              : {}
          }
          className={`font-mono text-sm font-medium ${getTimerColor()}`}
        >
          {formatTime()}
        </motion.span>
      </motion.div>
    </AnimatePresence>
  );
}
